import React from 'react'
import { List, ResponsiveTable } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import { useUser } from '@fs/zion-user'
import { type GroupSummary } from '@fs/zion-tree-types'
import { type GedcomFileFailed, type GedcomFile } from '../../../services/groupTypes'
import { ManageTreeRow, ManageTreeRowSkeleton } from './ManageTreeRow'
import { useAllTreeGroups } from '../cets/AllTreeGroupsProvider'

// Combine processing files and groups into a single sorted list
type ListItem = { type: 'processing'; data: GedcomFile } | { type: 'group'; data: GroupSummary }

type Props = {
  processingFiles?: GedcomFile[]
  groups: GroupSummary[]
  failedFiles?: GedcomFileFailed[]
  isMobile: boolean
  isFamilyGroupTree?: boolean
  showCombinedDesign: boolean
}

/** The single collation rule for every tree list on this page, so the orderings cannot drift apart. */
function compareNames(a: string, b: string): number {
  return a.toUpperCase().localeCompare(b.toUpperCase())
}

/** A processing file and a group hold their display name under different keys. */
function getItemName(item: ListItem): string {
  if (item.type === 'processing') return item.data.title
  return item.data.name
}

function byName(a: ListItem, b: ListItem): number {
  return compareNames(getItemName(a), getItemName(b))
}

/** Renders a set of trees, choosing the wrapper element and telling each row which shape to take. */

export default function ManageTreeList({
  processingFiles,
  groups,
  failedFiles,
  isMobile,
  isFamilyGroupTree,
  showCombinedDesign,
}: Props): JSX.Element | null {
  const [t] = useTranslation()
  const { uploadedFiles, loadingUploadedFiles } = useAllTreeGroups()
  const { cisId: currentUserCisId } = useUser()

  // The columned layout is desktop-only; at mobile width the trees stack as list items instead.
  const showTableLayout = showCombinedDesign && !isMobile

  if (loadingUploadedFiles) {
    return <ManageTreeListSkeleton isMobile={isMobile} />
  }

  function renderTreeRow(group: GroupSummary, isLast: boolean): JSX.Element {
    const file = uploadedFiles.find((f) => f.treeId === group.treeIds?.[0])
    return (
      <ManageTreeRow
        key={group.id || file?.fileName || group.name}
        asTableRow={showTableLayout}
        showCombinedDesign={showCombinedDesign}
        group={group}
        file={file}
        isMobile={isMobile}
        isLast={isLast}
        currentUserCisId={currentUserCisId}
      />
    )
  }

  function renderProcessingRow(file: GedcomFile, isLast: boolean): JSX.Element {
    return (
      <ManageTreeRow
        key={file.fileName}
        asTableRow={showTableLayout}
        showCombinedDesign={showCombinedDesign}
        processingFile={file}
        isMobile={isMobile}
        isLast={isLast}
      />
    )
  }

  function renderFailedRow(file: GedcomFileFailed, isLast: boolean): JSX.Element {
    return (
      <ManageTreeRow
        key={file.fileName}
        asTableRow={showTableLayout}
        showCombinedDesign={showCombinedDesign}
        failedFile={file}
        isMobile={isMobile}
        isLast={isLast}
      />
    )
  }

  const combinedItems: ListItem[] = [
    ...(processingFiles ?? []).map((file) => ({ type: 'processing' as const, data: file })),
    ...groups.map((group) => ({ type: 'group' as const, data: group })),
  ].sort(byName)

  const failed = failedFiles ?? []
  const rows: JSX.Element[] = [
    ...combinedItems.map((item, index) => {
      const isLast = index === combinedItems.length - 1 && failed.length === 0
      if (item.type === 'processing') return renderProcessingRow(item.data, isLast)
      return renderTreeRow(item.data, isLast)
    }),
    ...failed.map((file, index) => renderFailedRow(file, index === failed.length - 1)),
  ]

  if (!showTableLayout) {
    return <List>{rows}</List>
  }

  if (rows.length === 0) {
    return null
  }

  const tableHeaders = [
    isFamilyGroupTree ? t('group-management.name.label') : t('tree-import.manage-trees.table-tree-name-heading'),
    t('tree-import.manage-trees.table-date-heading'),
    t('tree-import.manage-trees.table-role-heading'),
    t('tree-import.manage-trees.table-tools-heading'),
  ]

  return (
    <ResponsiveTable
      verticalAlign="middle"
      stickyHeader={false}
      totalColumnCount={4}
      atSizeColumns={{ default: { labels: tableHeaders, columnWidths: [6, 2, 2, 2] } }}
    >
      {rows}
    </ResponsiveTable>
  )
}

export function ManageTreeListSkeleton({ isMobile }: { isMobile: boolean }): JSX.Element {
  return (
    <List>
      <ManageTreeRowSkeleton isMobile={isMobile} />
      <ManageTreeRowSkeleton isMobile={isMobile} />
      <ManageTreeRowSkeleton isMobile={isMobile} />
    </List>
  )
}
